import { FC, useState } from 'react';
import { format } from 'date-fns';
import { toValidDate } from '@libs/frontend-services';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LuCalendar } from 'react-icons/lu';
import { X } from 'lucide-react';

export const DatePicker: FC<{
  date?: Date;
  fromDate?: Date;
  setDate: (d?: Date) => void;
  allowClear?: boolean;
}> = ({ date, setDate, fromDate, allowClear = false }) => {
  // Imported order dates may be malformed; neither format nor Calendar accepts Invalid Date.
  const validDate = toValidDate(date) ?? undefined;
  const [open, setOpen] = useState<boolean>(false);
  const [month, setMonth] = useState<Date | undefined>(validDate);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDate(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Button
            type="button"
            variant={'outline'}
            className={cn(
              'w-full justify-start text-left font-normal bg-white text-text-light',
              !validDate && 'text-muted-foreground',
            )}
            onClick={() => setOpen(true)}
          >
            <LuCalendar className="mr-2 h-4 w-4 text-text-light" />
            {validDate ? (
              format(validDate, 'EEE, MMM dd, yyyy')
            ) : (
              <span className="text-text-light">Pick a date</span>
            )}
          </Button>
          {allowClear && validDate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-slate-100"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={validDate}
          month={month}
          onMonthChange={setMonth}
          onSelect={(d) => {
            setDate(d);
            setOpen(false);
          }}
          initialFocus
          fromDate={toValidDate(fromDate) ?? undefined}
          defaultMonth={validDate}
        />
      </PopoverContent>
    </Popover>
  );
};
